import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Row, Col, Button, Form, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import axios from '../../../api/axios';
import { Candidate, QuestionItem, SocialSkill, Survey } from '../../../types';
import { getRelationshipString, getPeriodString, getActivityFrequencyString, getJobTypeString, getPositionString } from '../../../utils/Constants';
import AdminHeader from '../layout/AdminHeader';
import AdminNavigation from '../layout/AdminNavigation';
import ImpersonationCheckModal, { SecurityInfo } from '../../../user/components/results/ImpersonationCheckModal';
import { getSurveys } from '../../../api/surveyApi';
import './AdminCandidateDetail.css';
import { getSocialSkills } from '../../../api/socialSkillApi';
import { formatSurveyResponse } from '../../../utils/Utils';

interface ResponseData {
  id: number;
  respondent_id: number;
  candidate_id: number;
  res1_relationship_private: string;
  res1_relationship_detail: number;
  res1_work_company: string;
  res1_work_details: string;
  res1_work_period_start_year: number;
  res1_work_period_start_season: number;
  res1_work_period_end_year: number | null;
  res1_work_period_end_season: number | null;
  res1_currently_working: boolean;
  res1_activity_frequency: number;
  res2_q1: string;
  res2_q2: string;
  res2_q3: string;
  res2_q4: string;
  res2_q5: string;
  res2_q6: string;
  res2_q7: string;
  res2_q8: string;
  res2_q9: string;
  res2_q10: string;
  res3_social_skill: number[];
  res4_q1: string;
  res4_q2: string;
  res4_q3: string;
  res4_q4: string;
  res4_q5: string;
  res4_q6: string;
  res4_q7: string;
  res4_q8: string;
  res4_q9: string;
  res4_q10: string;
  created_at: string;
  updated_at: string;
  respondent?: {
    id: number;
    candidate_id: number;
    name: string;
    email: string;
    relationship: string;
    browser_info: string;
    device_info: string;
    ip_address: string;
  };
}

const AdminCandidateDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [responses, setResponses] = useState<ResponseData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [dynamicQuestions, setDynamicQuestions] = useState<Array<QuestionItem>>([]);
  const [surveyQuestions, setSurveyQuestions] = useState<Array<Survey>>([]);
  const [socialSkillQuestions, setSocialSkillQuestions] = useState<Array<SocialSkill>>([]);
  const [showImpersonationModal, setShowImpersonationModal] = useState<boolean>(false);
  const [candidateSecurityInfo, setCandidateSecurityInfo] = useState<SecurityInfo>({
    name: '',
    browser_info: '',
    device_info: '',
    ip_address: ''
  });
  const [respondentSecurityInfo, setRespondentSecurityInfo] = useState<SecurityInfo>({
    name: '',
    browser_info: '',
    device_info: '',
    ip_address: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }

    fetchCandidateDetails();
  }, [id, navigate]);

  const fetchQuestionTemplate = async (questionTemplateId: number): Promise<void> => {
    try {
      const response = await axios.get(`/admin/question-templates/${questionTemplateId}`);
      const questions = response.data.questions || [];

      const sortedQuestions = questions.sort((a: QuestionItem, b: QuestionItem) => a.order - b.order);
      setDynamicQuestions(sortedQuestions);
    } catch (error: unknown) {
      console.error('Error fetching question template:', error);
    }
  };

  const fetchSurveys = async (): Promise<void> => {
    try {
      const surveyData = await getSurveys();
      if (surveyData && surveyData.length > 0) {
        const sortedQuestions = surveyData.sort((a: Survey, b: Survey) => a.order - b.order);
        setSurveyQuestions(sortedQuestions);
      }
    } catch (error: unknown) {
      console.error('Error fetching surveys:', error);
    }
  };

  const fetchSocialSkills = async (): Promise<void> => {
    try {
      const socialSkillData = await getSocialSkills();
      if (socialSkillData && socialSkillData.length > 0) {
        const sortedQuestions = socialSkillData.sort((a: SocialSkill, b: SocialSkill) => a.order - b.order);
        setSocialSkillQuestions(sortedQuestions);
      }
    } catch (error: unknown) {
      console.error('Error fetching social skills:', error);
      setSocialSkillQuestions([]);
    }
  };

  const fetchCandidateDetails = async () => {
    try {
      setLoading(true);

      const candidateResponse = await axios.get(`/admin/candidates/${id}`);
      setCandidate(candidateResponse.data);

      if (candidateResponse.data.question_template_id) {
        await fetchQuestionTemplate(candidateResponse.data.question_template_id);
      }

      await fetchSurveys();
      await fetchSocialSkills();

      const responsesResponse = await axios.get(`/admin/responses?candidate_id=${id}`);
      setResponses(responsesResponse.data.data || []);

      setCandidateSecurityInfo({
        name: candidateResponse.data.name,
        browser_info: candidateResponse.data.browser_info || '',
        device_info: candidateResponse.data.device_info || '',
        ip_address: candidateResponse.data.ip_address || ''
      });

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('候補者詳細の取得に失敗しました。');
      setLoading(false);
    }
  };

  const handleImpersonationCheck = (response: ResponseData) => {
    setRespondentSecurityInfo({
      name: response.respondent?.name || '',
      browser_info: response.respondent?.browser_info || '',
      device_info: response.respondent?.device_info || '',
      ip_address: response.respondent?.ip_address || ''
    });
    setShowImpersonationModal(true);
  };

  const compareText = (candidateValue: string, respondentValue: string): string => {
    if (!candidateValue || !respondentValue) return '不一致';
    if (candidateValue === '不明' || respondentValue === '不明') return '不一致';
    return candidateValue === respondentValue ? '一致' : '不一致';
  };

  const checkImpersonationText = (candidateInfo: SecurityInfo, respondentInfo: ResponseData): string => {
    if (!candidateInfo || !respondentInfo) return '低';
    if (candidateInfo.ip_address === respondentInfo.respondent?.ip_address) return '高';
    if (candidateInfo.browser_info === respondentInfo.respondent?.browser_info) return '中';
    if (candidateInfo.device_info === respondentInfo.respondent?.device_info) return '中';
    return '低';
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <AdminHeader />

        <Container>
          <AdminNavigation />
          <div className="blank-form-container">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">情報を読み込んでいます...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="dashboard-container">
        <AdminHeader />

        <Container>
          <AdminNavigation />
          <Alert variant="danger">候補者の詳細情報が見つかりません。</Alert>
        </Container>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <AdminHeader />

      <Container>
        <AdminNavigation />

        {error && <Alert variant="danger">{error}</Alert>}

        <h2 className="candidate-detail-title">{candidate.name}</h2>
        <div className="candidate-detail-section mb-4">
          <Row>
            <Col className="client-detail-label" md={2}>メールアドレス</Col>
            <Col className="client-detail-value" md={4}>{candidate.email}</Col>
            <Col className="client-detail-label" md={2}>電話番号</Col>
            <Col className="client-detail-value" md={4}>{candidate.phone || '-'}</Col>
          </Row>
          <Row>
            <Col className="client-detail-label" md={2}>職種</Col>
            <Col className="client-detail-value" md={4}>{candidate.job_type ? getJobTypeString(Number(candidate.job_type)) : '-'}</Col>
            <Col className="client-detail-label" md={2}>職位</Col>
            <Col className="client-detail-value" md={4}>{candidate.position ? getPositionString(Number(candidate.position)) : '-'}</Col>
          </Row>
          <Row>
            <Col className="client-detail-label" md={2}>配属先</Col>
            <Col className="client-detail-value" md={4}>{candidate.assignment_location || '-'}</Col>
            <Col className="client-detail-label" md={2}></Col>
            <Col className="client-detail-value" md={4}></Col>
          </Row>
        </div>

        {/* 社内向けメモ */}
        <p className="internal-memo-title">社内向けメモ</p>
        <Row className="mb-4">
          <Col md={8}>
            <Form.Control
              as="textarea"
              rows={3}
              value={candidate.internal_memo}
              placeholder=""
              className="form-textarea internal-memo"
              readOnly
            />
          </Col>
          <Col md={4} className='d-none'>
            <Button className="narisumashi-check">
              <FontAwesomeIcon icon={faDownload} className="me-2" />
              PDFダウンロード
            </Button>
          </Col>
        </Row>

        {responses.length > 0 ? (
          <>
            <Row className="mb-4">
              <Col>
                <div className="form-container">
                  <div className="form-header">
                    回答者情報
                  </div>
                  <div className="form-body">
                    {responses.map((response, index) => (
                      <div key={`respondent-info-${index}`} className="respondent-relation-container mb-4">
                        <Row className='mb-3'>
                          <Col><h4 className="respondent-name">{index + 1}人目</h4></Col>
                        </Row>
                        <Row>
                          <Col md={4}>
                            <div className='respondent-name-row'>
                              <h4 className="respondent-name">{response.respondent?.name || `回答者${index + 1}`}</h4>さん
                            </div>
                            <div className="relationship-tag">
                              前職の{getRelationshipString(Number(response.res1_relationship_detail))}
                            </div>

                            <div className="evaluation-items">
                              <Button className="narisumashi-check" onClick={() => handleImpersonationCheck(response)}>
                                なりすましチェック
                              </Button>
                              <div className="evaluation-item">
                                <span className="evaluation-item-label">ブラウザ：</span>
                                <span className="evaluation-item-value">{compareText(candidateSecurityInfo.browser_info, response.respondent?.browser_info || '')}</span>
                              </div>
                              <div className="evaluation-item">
                                <span className="evaluation-item-label">IPアドレス：</span>
                                <span className="evaluation-item-value">{compareText(candidateSecurityInfo.ip_address, response.respondent?.ip_address || '')}</span>
                              </div>
                              <div className="evaluation-item">
                                <span className="evaluation-item-label">端末：</span>
                                <span className="evaluation-item-value">{compareText(candidateSecurityInfo.device_info, response.respondent?.device_info || '')}</span>
                              </div>
                              <div className="evaluation-item">
                                <span className="evaluation-item-label">総合評価</span>
                                <span className="evaluation-item-value">なりすまし可能性：{checkImpersonationText(candidateSecurityInfo, response)}</span>
                              </div>
                            </div>
                          </Col>
                          <Col md={8}>
                            <div className="project-info">
                              <div className="project-detail">
                                <strong>過ごした時間：</strong><div>{response.res1_relationship_private === 'work' ? '仕事やプライベートを一緒にした時間の方が長い' : 'プライベートで一緒に過ごした時間の方が長い'}</div>
                              </div>
                              <div className="project-detail">
                                <strong>所属：</strong> <div>{response.res1_work_company}</div>
                              </div>
                              <div className="project-detail">
                                <strong>関係：</strong> <div>{getRelationshipString(Number(response.res1_relationship_detail))}</div>
                              </div>
                              <div className="project-detail">
                                <strong>期間：</strong> 
                                <div>
                                  {response.res1_work_period_start_year ?? ''}年 {getPeriodString(Number(response.res1_work_period_start_season))}〜
                                  {response.res1_currently_working
                                    ? '現在'
                                    : ` ${response.res1_work_period_end_year ?? ''}年 ${getPeriodString(Number(response.res1_work_period_end_season))}`
                                  }
                                </div>
                              </div>
                              <div className="project-detail">
                                <strong>頻度：</strong> {getActivityFrequencyString(Number(response.res1_activity_frequency))}
                              </div>
                              <div className="project-detail">
                                <strong>詳細：</strong> <div>{response.res1_work_details}</div>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    ))}
                  </div>
                </div>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col>
                <div className="form-container">
                  <div className="form-header">
                    記述式質問に対する回答
                  </div>
                  <div className="form-body">
                    {dynamicQuestions.length > 0 ? (
                      dynamicQuestions.map((question: QuestionItem, questionIndex) => (
                        <div key={`question-${questionIndex + 1}`} className="question-container mb-3">
                          <div className="question-header">
                            Q{questionIndex + 1}. {question.text.replace(/{candidate_name}|候補者/g, candidate?.name || '')}
                          </div>

                          {responses.map((response, responseIndex) => (
                            <div key={`q${questionIndex + 1}-response-${responseIndex}`} className="response-item">
                              <Row>
                                <Col md={2} className="response-author-col">
                                  <span className="response-author">{response.respondent?.name || `回答者${responseIndex + 1}`}</span><small>さん</small>
                                </Col>
                                <Col md={10} className="response-content-col">
                                  <p className="response-content">{String(response[`res2_q${questionIndex + 1}` as keyof ResponseData] || '')}</p>
                                </Col>
                              </Row>
                            </div>
                          ))}
                        </div>
                      ))
                    ) : (
                      <div className="text-center">質問が設定されていません。</div>
                    )}
                  </div>
                </div>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col>
                <div className="form-container">
                  <div className="form-header">
                    選択式質問に対する回答
                  </div>
                  <div className="form-body">
                    {socialSkillQuestions.length > 0 ? (
                      (() => {
                        const categories = [
                          { name: 'コンプライアンス', startIndex: 0, endIndex: 2 },
                          { name: '勤怠', startIndex: 3, endIndex: 5 },
                          { name: '責任感', startIndex: 6, endIndex: 8 },
                          { name: 'ストレス耐性', startIndex: 9, endIndex: 11 },
                          { name: 'コミュニケーション', startIndex: 12, endIndex: 14 }
                        ];

                        const getCategoryProblemsCount = (startIndex: number, endIndex: number) => {
                          return responses.filter(response => 
                            response.res3_social_skill?.slice(startIndex, endIndex + 1).some(value => value === 1 || value === 2)
                          ).length;
                        };

                        return categories.map((category, categoryIndex) => {
                          const problemsCount = getCategoryProblemsCount(category.startIndex, category.endIndex);
                          const categoryQuestions = socialSkillQuestions.slice(category.startIndex, category.endIndex + 1);

                          return (
                            <div key={`category-${categoryIndex}`} className="social-skills-category-group mb-4">
                              <div className="category-header">
                                {category.name}
                              </div>

                              <div className="category-questions mb-3">
                                {categoryQuestions.map((question, questionIndex) => (
                                  <div key={`question-${category.startIndex + questionIndex + 1}`} className="category-question">
                                    Q{category.startIndex + questionIndex + 1}. {question.text}
                                  </div>
                                ))}
                              </div>

                              <div className={`category-evaluation-summary ${problemsCount > 0 ? 'has-problems' : 'no-problems'}`}>
                                {problemsCount > 0 ? `${problemsCount}名の回答者が問題ありと評価` : `${responses.length-problemsCount}名の回答者が問題なしと評価`}
                              </div>
                            </div>
                          );
                        });
                      })()
                    ) : (
                      <div className="text-center">質問が設定されていません。</div>
                    )}
                  </div>
                </div>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col>
                <div className="form-container">
                  <div className="form-header">
                    アンケートの回答
                  </div>
                  <div className="form-body">
                    {surveyQuestions.length > 0 ? (
                      responses.map((response, responseIndex) => (
                        <div key={`survey-response-${responseIndex}`} className="respondent-survey-container mb-4">
                          <div className='respondent-name-row'>
                            <h4 className="respondent-name">{response.respondent?.name || `回答者${responseIndex + 1}`}</h4>さん
                          </div>

                          <div className="survey-questions">
                            {surveyQuestions.length > 0 ? (
                              surveyQuestions.map((question: Survey, questionIndex) => {
                                const fieldValue = response[`res4_q${questionIndex + 1}` as keyof ResponseData];
                                const displayValue = formatSurveyResponse(fieldValue, question.input_type);
                                
                                return (
                                  <div key={`response-${responseIndex}-q${questionIndex + 1}`} className="survey-question">
                                    <div className="survey-question-text">Q{questionIndex + 1}. {question.text}</div>
                                    <p>{displayValue}</p>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="text-center">アンケートが設定されていません。</div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center">アンケートが設定されていません。</div>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </>
        ) : (
          <div className="form-container">
            <div className="form-header">
              リファレンスチェック結果
            </div>
            <div className="form-body">
              <p className="text-center">まだリファレンスチェックの回答がありません。</p>
            </div>
          </div>
        )}

      </Container>

      <ImpersonationCheckModal
        show={showImpersonationModal}
        onHide={() => setShowImpersonationModal(false)}
        candidateSecurityInfo={candidateSecurityInfo}
        respondentSecurityInfo={respondentSecurityInfo}
      />
    </div>
  );
};

export default AdminCandidateDetail;
